import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identityUsersApi } from '@/api/services/identityUsers';
import type {
  IdentityUserCreateDto,
  IdentityUserUpdateDto,
  SetActiveDto,
  UpdatePermissionsDto,
} from '@/api/types/identity';

export const identityUserKeys = {
  all: ['identityUsers'] as const,
  list: ['identityUsers', 'list'] as const,
  assignableRoles: ['identityUsers', 'assignableRoles'] as const,
  user: (id: string) => ['identityUsers', 'user', id] as const,
  userRoles: (id: string) => ['identityUsers', 'userRoles', id] as const,
  permissions: (id: string) => ['identityUsers', 'permissions', id] as const,
};

export function useAssignableRoles(enabled = true) {
  return useQuery({
    queryKey: identityUserKeys.assignableRoles,
    queryFn: () => identityUsersApi.getAssignableRoles(),
    enabled,
  });
}

export function useUser(id: string | null) {
  return useQuery({
    queryKey: identityUserKeys.user(id ?? ''),
    queryFn: () => identityUsersApi.get(id as string),
    enabled: id !== null,
  });
}

export function useUserRoles(id: string | null) {
  return useQuery({
    queryKey: identityUserKeys.userRoles(id ?? ''),
    queryFn: () => identityUsersApi.getRoles(id as string),
    enabled: id !== null,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IdentityUserCreateDto) => identityUsersApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityUserKeys.list }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: IdentityUserUpdateDto }) =>
      identityUsersApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityUserKeys.list }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => identityUsersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityUserKeys.list }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SetActiveDto }) =>
      identityUsersApi.setActive(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityUserKeys.list }),
  });
}

export function useSendUserVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => identityUsersApi.sendEmailVerification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityUserKeys.list }),
  });
}

export function useUserPermissions(id: string | null) {
  return useQuery({
    queryKey: identityUserKeys.permissions(id ?? ''),
    queryFn: () => identityUsersApi.getPermissions(id as string),
    enabled: id !== null,
  });
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePermissionsDto }) =>
      identityUsersApi.updatePermissions(id, input),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: identityUserKeys.permissions(variables.id) }),
  });
}
